import { z } from 'zod';

import { baseEntitySchema } from './baseContract';
import { organizationSchema } from './organizationContract';
import {
  type SubscriptionTier,
  BillingInterval,
  billingIntervalEnum,
  subscriptionItemProductTypeEnum,
  subscriptionStatusEnum,
  subscriptionTierEnum,
  type Currency,
  Currency as CurrencyEnum,
  SubscriptionTier as SubscriptionTierEnum,
  BillingInterval as BillingIntervalEnum,
} from '../enums';

// Helper to preprocess dates that might be strings
// FIXME WTF is this? nikde s enepoužívá parse, tohle je k ničemu
const preprocessDate = (baseSchema: z.ZodDate | z.ZodNullable<z.ZodDate>) =>
  z.preprocess(
    arg => {
      if (typeof arg === 'string') {
        // Attempt to parse strings into Date objects and return directly
        return new Date(arg);
      }

      // Pass through other types (like actual Date objects or null/undefined)
      return arg;
    },
    // Apply base schema THEN refine to check for valid Date objects
    baseSchema.refine(
      val => {
        // If schema allows null and value is null, it's valid
        if (val === null && baseSchema instanceof z.ZodNullable) {
          return true;
        }

        // Otherwise, ensure it's a valid Date instance
        return val instanceof Date && !Number.isNaN(val.getTime());
      },
      {
        message: 'Invalid date string or format provided',
      },
    ),
  );

export const subscriptionSchema = baseEntitySchema.extend({
  tier: subscriptionTierEnum.optional(),
  status: subscriptionStatusEnum,
  maxDocuments: z.number().min(0).optional(),
  maxUsers: z.number().min(0).optional(),
  price: z.number().min(0).optional(),
  autoRenewal: z.boolean().default(false),
  autoRenewalLimit: z.number().nullable(),
  autoRenewalAmount: z.number().nullable(),
  startDate: preprocessDate(z.date()),
  endDate: preprocessDate(z.date().nullable()),
  billingInterval: billingIntervalEnum.default(BillingInterval.Month),
  /** Interval multiplier: 1 monthly/yearly, 3 quarterly, 6 half-yearly. */
  billingIntervalCount: z.number().int().min(1).default(1),
  stripeSubscriptionId: z.string().nullable(),
  stripePriceId: z.string().nullable(),
  stripeProductId: z.string().nullable(),
  metadata: z.record(z.string(), z.string().optional()).nullable(),
  autoRenewalChargedThisPeriod: z.number().min(0).default(0),
  autoRenewalPeriodStart: preprocessDate(z.date().nullable()),
  autoRenewalThreshold: z
    .number()
    .positive({ message: 'Threshold must be positive' })
    .min(5, { message: 'Threshold must be at least $5.00' })
    .nullable()
    .optional(),
  syncedAt: preprocessDate(z.date().nullable())
    .refine(date => !date || date <= new Date(), {
      message: 'Sync timestamp cannot be in the future',
    })
    .optional(),
  cancelsAtPeriodEnd: z.boolean().nullable().optional(),
});

export type SubscriptionDTO = z.infer<typeof subscriptionSchema>;

export const createSubscriptionSchema = subscriptionSchema
  .omit({
    id: true,
    createdAt: true,
    updatedAt: true,
  })
  .required({
    tier: true,
    status: true,
  })
  .strict();

export const updateSubscriptionSchema = subscriptionSchema
  .partial()
  .required({
    id: true,
  })
  .strict();

export type CreateSubscriptionDTO = z.infer<typeof createSubscriptionSchema>;
export type UpdateSubscriptionDTO = z.infer<typeof updateSubscriptionSchema>;

/**
 * Represents detailed information about a subscription tier,
 * combining static config and live pricing/metadata.
 */
export interface TierDetails {
  id: SubscriptionTier;
  name: string;
  description: string;
  features: string[];
  maxDocuments: number | string;
  maxUsers: number | string;
  maxConversations: number | string;
  /** Display-only: max pages from Stripe metadata. Only shown on plan card when defined. */
  maxPages?: number | string;
  /** Display-only marketing metric: how many colleagues this plan substitutes (e.g. 0.5). Not tied to any enforcement logic. */
  colleaguesSubstitute: number | string;
  prices: Array<{
    id: string;
    amount: number;
    interval: BillingInterval | null;
    currency: Currency;
  }>;
  metadata: Record<string, string>;
}

export const createCheckoutInputSchema = z.object({
  organizationId: z.string().uuid(),
  tier: subscriptionTierEnum,
  billingInterval: billingIntervalEnum.default(BillingInterval.Month),
  successUrl: z.string().url(),
  cancelUrl: z.string().url(),
});

export const updateSubscriptionInputSchema = z.object({
  organizationId: z.string().uuid(),
  tier: subscriptionTierEnum.optional(),
  billingInterval: billingIntervalEnum.optional(),
  autoRenewal: z.boolean().optional(),
  maxUsers: z.number().min(1).optional(),
  maxDocuments: z.number().positive().optional(),
  autoRenewalAmount: z
    .number()
    .positive()
    .min(5, { message: 'Auto-renewal amount must be at least $5' })
    .nullable()
    .optional()
    .transform(val => val ?? undefined),
  autoRenewalLimit: z
    .number()
    .nonnegative()
    .nullable()
    .optional()
    .transform(val => val ?? undefined),
  autoRenewalThreshold: z
    .number()
    .positive({ message: 'Threshold must be positive' })
    .min(5, { message: 'Threshold must be at least $5.00' })
    .nullable()
    .optional()
    .transform(val => val ?? undefined),
});

export const getActiveInputSchema = z.object({
  organizationId: z.string().uuid(),
});

export const getActiveOutputSchema = subscriptionSchema.extend({
  organization: organizationSchema,
});

export const cancelSubscriptionInputSchema = z.object({
  organizationId: z.string().uuid(),
  /**
   * When true, schedule cancellation at the end of the current paid period
   * (Stripe `cancel_at_period_end`) — reversible via `resumeSubscription`, and
   * available to org admins / partners. When false (default), cancel
   * immediately — SuperAdmin only.
   */
  atPeriodEnd: z.boolean().default(false),
});

export const resumeSubscriptionInputSchema = z.object({
  organizationId: z.string().uuid(),
});

/**
 * Auto top-up (auto-renewal) settings update. Amounts are in **base credits**
 * (the frontend converts from the display currency). Used by the shared
 * settings component on both the customer payments page and the SuperAdmin /
 * partner plan-management page.
 */
export const updateAutoRenewalInputSchema = z.object({
  organizationId: z.string().uuid(),
  autoRenewal: z.boolean(),
  autoRenewalAmount: z.number().positive().nullable().optional(),
  autoRenewalLimit: z.number().min(0).nullable().optional(),
  autoRenewalThreshold: z.number().positive().nullable().optional(),
});

export const createBillingPortalInputSchema = z.object({
  organizationId: z.string().uuid(),
  returnUrl: z.string().url().optional(),
});

export const topUpBalanceInputSchema = z.object({
  organizationId: z.string().uuid(),
  amount: z.number().min(5),
  successUrl: z.string().url(),
  cancelUrl: z.string().url(),
});

/**
 * Sales intent for how the first/renewal invoices are collected.
 * The actual renewal path is decided by the real payment method on the first
 * `invoice.paid` (see plan §"SuperAdmin card assign").
 */
export const collectionMethodTargetEnum = z.enum(['card', 'bank_transfer']);
export type CollectionMethodTarget = z.infer<typeof collectionMethodTargetEnum>;

/**
 * A single line item for `assignPlan`. Exactly one base-plan item (a price whose
 * product has `tier` metadata and is not `type: addon`) is required; any number of
 * recurring add-ons may follow. One-time setup fees are passed via
 * `oneTimeFeePriceIds` instead, because Stripe bills them on the first invoice only.
 */
export const assignPlanItemSchema = z.object({
  stripePriceId: z.string().startsWith('price_'),
  productType: z.enum(['plan', 'addon']).default('plan'),
  addonKey: z.string().optional(),
  quantity: z.number().int().min(1).default(1),
});

export type AssignPlanItemDTO = z.infer<typeof assignPlanItemSchema>;

export const assignPlanInputSchema = z.object({
  organizationId: z.string().uuid(),
  items: z.array(assignPlanItemSchema).min(1),
  oneTimeFeePriceIds: z.array(z.string().startsWith('price_')).optional(),
  collectionMethodTarget: collectionMethodTargetEnum,
  daysUntilDue: z.number().int().min(1).max(60).default(14),
  duzpDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'DUZP date must be in YYYY-MM-DD format')
    .optional(),
  sendNow: z.boolean().default(false),
  /**
   * When true, an existing active/past_due subscription is canceled and replaced.
   * Required to overwrite a conflicting subscription (guards against accidental loss).
   */
  replaceExisting: z.boolean().default(false),
});

export const assignPlanOutputSchema = z.object({
  subscriptionId: z.string(),
  stripeSubscriptionId: z.string(),
  invoiceId: z.string().startsWith('in_'),
  invoiceStatus: z.string(),
  hostedInvoiceUrl: z.string().url().nullable(),
  /** due_date + 30d, capped at finalization + 120d. Null when not yet finalized. */
  hostedInvoiceUrlExpiresAt: z.string().nullable(),
  sent: z.boolean(),
});

export type AssignPlanInputDTO = z.infer<typeof assignPlanInputSchema>;
export type AssignPlanOutputDTO = z.infer<typeof assignPlanOutputSchema>;

export const markInvoicePaidInputSchema = z.object({
  invoiceId: z.string().startsWith('in_'),
});

export const voidInvoiceInputSchema = z.object({
  invoiceId: z.string().startsWith('in_'),
});

export const voidAndReissueInvoiceInputSchema = z.object({
  invoiceId: z.string().startsWith('in_'),
  /** Optional corrected DUZP (tax point) for the reissued invoice. */
  duzpDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'DUZP date must be in YYYY-MM-DD format')
    .optional(),
  sendNow: z.boolean().default(false),
});

export const refreshInvoiceUrlInputSchema = z.object({
  invoiceId: z.string().startsWith('in_'),
});

export const refreshInvoiceUrlOutputSchema = z.object({
  invoiceId: z.string().startsWith('in_'),
  hostedInvoiceUrl: z.string().url().nullable(),
  hostedInvoiceUrlExpiresAt: z.string().nullable(),
});

export const reissueInvoiceOutputSchema = z.object({
  invoiceId: z.string().startsWith('in_'),
  hostedInvoiceUrl: z.string().url().nullable(),
  hostedInvoiceUrlExpiresAt: z.string().nullable(),
  status: z.string(),
  sent: z.boolean(),
});

export type ReissueInvoiceOutputDTO = z.infer<
  typeof reissueInvoiceOutputSchema
>;

export const listInvoicesInputSchema = z.object({
  organizationId: z.string().uuid(),
  /** When omitted, returns the full ledger (open, draft, void, paid, uncollectible). */
  statuses: z
    .array(z.enum(['draft', 'open', 'paid', 'uncollectible', 'void']))
    .optional(),
});

export const invoiceListItemSchema = z.object({
  invoiceId: z.string().startsWith('in_'),
  number: z.string().nullable(),
  amountDue: z.number(),
  amountPaid: z.number(),
  currency: z.string(),
  dueDate: z.string().nullable(),
  hostedInvoiceUrl: z.string().url().nullable(),
  hostedInvoiceUrlExpiresAt: z.string().nullable(),
  status: z.string(),
  createdAt: z.string(),
  /** Stripe `billing_reason` (subscription_create, subscription_cycle, manual, …). */
  billingReason: z.string().nullable(),
  /** Stripe subscription this invoice belongs to, or null for standalone reissues. */
  subscriptionId: z.string().nullable(),
  /** True for standalone reissue invoices (linked via metadata only). */
  isReissue: z.boolean(),
  duzpDate: z.string().nullable(),
  /** Whole days past `due_date` for open invoices, else 0. */
  daysOverdue: z.number(),
});

export type MarkInvoicePaidInputDTO = z.infer<
  typeof markInvoicePaidInputSchema
>;
export type VoidInvoiceInputDTO = z.infer<typeof voidInvoiceInputSchema>;
export type VoidAndReissueInvoiceInputDTO = z.infer<
  typeof voidAndReissueInvoiceInputSchema
>;
export type RefreshInvoiceUrlInputDTO = z.infer<
  typeof refreshInvoiceUrlInputSchema
>;
export type RefreshInvoiceUrlOutputDTO = z.infer<
  typeof refreshInvoiceUrlOutputSchema
>;
export type ListInvoicesInputDTO = z.infer<typeof listInvoicesInputSchema>;
export type InvoiceListItemDTO = z.infer<typeof invoiceListItemSchema>;

// --- Plan catalog (Phase C): unified base plans + add-ons for assignPlan ---

export const planCatalogPriceSchema = z.object({
  priceId: z.string(),
  interval: billingIntervalEnum,
  /** 1 monthly/yearly, 3 quarterly, 6 half-yearly. */
  intervalCount: z.number().int().min(1),
  /** Amount in major currency units (e.g. CZK), not cents. */
  amount: z.number(),
  currency: z.string(),
});

export const planCatalogBasePlanSchema = z.object({
  tier: subscriptionTierEnum,
  productId: z.string(),
  productName: z.string(),
  isCustomEnterprise: z.boolean(),
  features: z.array(z.string()),
  prices: z.array(planCatalogPriceSchema),
});

export const planCatalogAddonSchema = z.object({
  addonKey: z.string(),
  productId: z.string(),
  productName: z.string(),
  features: z.array(z.string()),
  prices: z.array(planCatalogPriceSchema),
  oneTimePrices: z.array(
    z.object({
      priceId: z.string(),
      amount: z.number(),
      currency: z.string(),
    }),
  ),
});

export const planCatalogSchema = z.object({
  basePlans: z.array(planCatalogBasePlanSchema),
  addons: z.array(planCatalogAddonSchema),
});

export const getPlanCatalogInputSchema = z.object({
  currency: z.nativeEnum(CurrencyEnum),
  /**
   * Required for partner (non-SuperAdmin) callers so the catalog can be
   * authorized against the client org and stripped of custom enterprise plans.
   */
  organizationId: z.string().uuid().optional(),
});

export type PlanCatalogPriceDTO = z.infer<typeof planCatalogPriceSchema>;
export type PlanCatalogBasePlanDTO = z.infer<typeof planCatalogBasePlanSchema>;
export type PlanCatalogAddonDTO = z.infer<typeof planCatalogAddonSchema>;
export type PlanCatalogDTO = z.infer<typeof planCatalogSchema>;

// --- Current subscription line items (read-only, synced from Stripe) ---

export const listSubscriptionItemsInputSchema = z.object({
  organizationId: z.string().uuid(),
});

export const subscriptionItemListItemSchema = z.object({
  id: z.string(),
  stripePriceId: z.string(),
  productType: subscriptionItemProductTypeEnum,
  addonKey: z.string().nullable(),
  productName: z.string(),
  quantity: z.number().int(),
  unitAmount: z.number(),
  currency: z.string(),
  interval: billingIntervalEnum,
  intervalCount: z.number().int(),
  currentPeriodStart: z.string().nullable(),
  currentPeriodEnd: z.string().nullable(),
});

export type ListSubscriptionItemsInputDTO = z.infer<
  typeof listSubscriptionItemsInputSchema
>;
export type SubscriptionItemListItemDTO = z.infer<
  typeof subscriptionItemListItemSchema
>;

export type SubscriptionCheckoutFrontendInput = Omit<
  z.infer<typeof createCheckoutInputSchema>,
  'organizationId'
>;

export type SubscriptionUpdateFrontendInput = Omit<
  z.infer<typeof updateSubscriptionInputSchema>,
  'organizationId'
>;

// --- Add Schema for Currency/Plan Change ---
export const changeCurrencyAndPlanInputSchema = z.object({
  organizationId: z.string().uuid('Invalid Organization ID'),
  targetCurrency: z.nativeEnum(CurrencyEnum),
  targetTier: z.nativeEnum(SubscriptionTierEnum),
  targetBillingInterval: z.nativeEnum(BillingIntervalEnum),
  // Price ID is optional, primarily for assigning specific custom Enterprise plans during switch
  targetPriceId: z.string().startsWith('price_').optional(),
});

export type ChangeCurrencyAndPlanInputDTO = z.infer<
  typeof changeCurrencyAndPlanInputSchema
>;
// --- Add Manual Balance Schema ---
export const addManualBalanceInputSchema = z.object({
  organizationId: z.string().min(1, 'Organization ID is required'),
  amountToAdd: z.number().positive('Amount must be positive').finite().safe(),
  currency: z.nativeEnum(CurrencyEnum),
  reason: z.string().optional(), // Optional: Add a reason for auditing
});

/**
 *  Schema for updating subscription via Stripe
 */
export const updateSubscriptionLimitsSchema = z.object({
  organizationId: z.string().uuid('Invalid organization ID'),
  maxUsers: z.number().min(1).step(1).optional(),
  maxDocuments: z.number().min(0).step(1).optional(),
});
